import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Alert, 
  LinearProgress, 
  Button,
  IconButton,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import roleService from '../../services/RoleService';
import HRService from '../../services/HrService';
import appService from '../../services/ApplicationService';

// Add global animation styles
const animationStyles = `
  @keyframes pulse {
    0% { opacity: 0.6; transform: scale(0.98); }
    50% { opacity: 1; transform: scale(1.02); }
    100% { opacity: 0.6; transform: scale(0.98); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const BulkRoleAssignmentUpload = ({ appId, onSuccess, unassignedRoles, mappedRoles }) => {
  // Add global styles when component mounts
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = animationStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      // Clean up style when component unmounts
      document.head.removeChild(styleElement);
    };
  }, []);

  const [roleAssignmentData, setRoleAssignmentData] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [fileProcessed, setFileProcessed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [roleOwnerList, setRoleOwnerList] = useState([]);
  const [unassignedRolesList, setUnassignedRolesList] = useState([]);
  const [assignedRolesList, setAssignedRolesList] = useState([]);
  const [appName, setAppName] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = assign new, 1 = edit existing
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [copySuccess, setCopySuccess] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  // Required columns for role assignment upload
  const requiredColumns = ['APP_NAME', 'ROLE_NAME', 'ROLE_OWNER_EMAIL'];

  // Reset file processed flag when component mounts and fetch app details
  useEffect(() => {
    setFileProcessed(false);
    fetchRoleOwners();
    fetchAppDetails();
    
    // Handle both unassigned and mapped roles
    if (unassignedRoles && unassignedRoles.length > 0) {
      setUnassignedRolesList(unassignedRoles);
    }
    
    if (mappedRoles && mappedRoles.length > 0) {
      setAssignedRolesList(mappedRoles);
    }
    
  }, [unassignedRoles, mappedRoles, appId]);

  const fetchAppDetails = async () => {
    try {
      const appDetails = await appService.fetchAppsById(appId);
      if (appDetails && appDetails.APP_NAME) {
        setAppName(appDetails.APP_NAME);
      }
    } catch (error) {
      console.error('Error fetching app details:', error);
    }
  };

  const fetchRoleOwners = async () => {
    try {
      // Just use fetchAllHRRecords directly since active_employees endpoint isn't working
      console.log('Fetching all HR records to get active employees');
      const hrRecords = await HRService.fetchAllHRRecords();
      console.log('HR records response:', hrRecords);
      
      if (hrRecords && Array.isArray(hrRecords)) {
        // Filter for active employees
        const activeHrRecords = hrRecords.filter(record => 
          record.STATUS && record.STATUS.toLowerCase() === 'active'
        );
        
        console.log(`Filtered ${activeHrRecords.length} active employees from HR records`);
        
        // Format them with the same format as in RoleMatrix.js
        const formattedActiveEmployees = activeHrRecords.map(emp => ({
          value: emp.id, // Use ID as the value to match RoleMatrix.js
          label: `${emp.FIRST_NAME} ${emp.LAST_NAME} (${emp.EMAIL_ADDRESS})`,
          firstName: emp.FIRST_NAME,
          lastName: emp.LAST_NAME,
          id: emp.id,
          email: emp.EMAIL_ADDRESS
        }));
        
        setRoleOwnerList(formattedActiveEmployees);
      } else {
        console.error('No HR records found or invalid response format');
      }
    } catch (error) {
      console.error('Error fetching role owners:', error);
      // Add more detailed error logging
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Handle drag over event
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  // Handle file drop event
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // Handle file input change event
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // Clear file and reset state
  const handleClearFile = () => {
    setFileName('');
    setRoleAssignmentData([]);
    setValidationError('');
    setFileProcessed(false);
    setSubmissionSuccess(false);
    setSubmissionError('');
    setPage(1);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Download template with unassigned roles
  const handleDownloadTemplate = () => {
    // Create a prominent header with the application name
    const templateHeader = [
      `"ROLE ASSIGNMENT TEMPLATE FOR: ${appName || 'Unknown Application'}"`,
      `"Application ID: ${appId}"`,
      '""',
      '""'
    ].join(',');
    
    // Add instructions based on active tab
    const instructions = [
      '"INSTRUCTIONS"',
      activeTab === 0 
        ? '"1. Do not modify the role names - they are pre-filled with unassigned roles"'
        : '"1. Do not modify the role names - they are pre-filled with currently assigned roles"',
      '"2. Enter email addresses of role owners in the ROLE_OWNER_EMAIL column"',
      '"3. Save the file and upload it to complete the assignments"',
      '""'
    ].join(',');
    
    const emptyRow = ['""', '""', '""'].join(',');
    
    // Create simpler CSV content - we'll hide APP_NAME as metadata
    const headers = ['ROLE_NAME', 'ROLE_OWNER_EMAIL', 'NOTES'].join(',');
    
    // Create data rows based on the active tab
    const rolesList = activeTab === 0 ? unassignedRolesList : assignedRolesList;
    const dataRows = rolesList.map(role => {
      // We're still storing appId as metadata that will be parsed on upload
      const metadata = `<!-- APP_NAME:${appId} -->`;
      
      // If this is an assigned role, pre-fill the current owner's email
      let ownerEmail = '""';
      if (activeTab === 1 && role.ROLE_OWNERS) {
        // Find the owner in the roleOwnerList
        const owner = roleOwnerList.find(o => o.value === role.ROLE_OWNERS);
        if (owner && owner.email) {
          ownerEmail = `"${owner.email}"`;
        }
      }
      
      return [
        `"${role.ROLE_NAME || role.value}"`,
        ownerEmail,
        `"${metadata}"`
      ].join(',');
    });
    
    const csvContent = [templateHeader, instructions, emptyRow, headers, ...dataRows].join('\n');
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const fileNameSuffix = activeTab === 0 ? 'new_assignments' : 'edit_assignments';
    const downloadFileName = appName ? 
      `role_assignment_template_${appName}_${fileNameSuffix}.csv` : 
      `role_assignment_template_${appId}_${fileNameSuffix}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', downloadFileName);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file upload logic
  const handleFile = (file) => {
    // Reset validation state
    setValidationError('');
    setIsValidating(true);
    setPage(1);
    setSubmissionSuccess(false);
    setSubmissionError('');
    
    if (!file) {
      setIsValidating(false);
      return;
    }
    
    const fileType = file.type;

    if (fileType === 'text/csv') {
      setFileName(file.name); // Display file name
      parseCSV(file);
    } else {
      setValidationError('File is not supported. Make sure to upload a CSV file.');
      setIsValidating(false);
    }
  };

  // CSV Parsing function (handling quotes and commas)
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const rows = content.split('\n');

        // Filter out metadata rows (first few rows that contain template header, etc.)
        const headerRowIndex = rows.findIndex(row => row.includes('ROLE_NAME,ROLE_OWNER_EMAIL'));
        
        if (headerRowIndex === -1 || rows.length < headerRowIndex + 2) {
          setValidationError('CSV file is not properly formatted or is missing data rows');
          setRoleAssignmentData([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse header row
        const header = parseCSVRow(rows[headerRowIndex]);
        
        // Check if we have at least ROLE_NAME and ROLE_OWNER_EMAIL
        const requiredColumnsInTemplate = ['ROLE_NAME', 'ROLE_OWNER_EMAIL'];
        const missingColumns = requiredColumnsInTemplate.filter(col => !header.includes(col));
        if (missingColumns.length > 0) {
          setValidationError(`Missing required columns: ${missingColumns.join(', ')}`);
          setRoleAssignmentData([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse the rest of the rows and reconstruct with APP_NAME
        const data = rows.slice(headerRowIndex + 1)
          .filter(row => row.trim() !== '') // Filter out empty rows
          .map((row) => {
            const values = parseCSVRow(row);
            const rowData = {};
            
            // Reconstruct row data with visible columns
            header.forEach((col, index) => {
              rowData[col.trim()] = values[index]?.trim() || ''; // Handling missing values
            });
            
            // Extract APP_NAME from metadata in NOTES column
            if (rowData.NOTES && rowData.NOTES.includes('<!-- APP_NAME:')) {
              const match = rowData.NOTES.match(/<!-- APP_NAME:(.*?) -->/);
              if (match && match[1]) {
                rowData.APP_NAME = match[1];
              } else {
                rowData.APP_NAME = appId; // Default to current appId if metadata parsing fails
              }
            } else {
              rowData.APP_NAME = appId; // Default to current appId if no metadata
            }
            
            return rowData;
          });

        // Validate that there is at least one row of data
        if (data.length === 0) {
          setValidationError('CSV file contains headers but no data rows');
          setRoleAssignmentData([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Select the appropriate roles list based on the active tab
        const rolesToValidateAgainst = activeTab === 0 ? unassignedRolesList : assignedRolesList;
        
        // Validate ROLE_NAME against the appropriate roles list
        const invalidRoleRows = data.filter(
          row => !rolesToValidateAgainst.some(
            role => (role.value || role.ROLE_NAME) === row.ROLE_NAME
          )
        );
        
        if (invalidRoleRows.length > 0) {
          const invalidRoles = [...new Set(invalidRoleRows.map(row => row.ROLE_NAME))].slice(0, 3);
          const errorMessage = activeTab === 0 
            ? `Invalid role names found: ${invalidRoles.map(role => `"${role}"`).join(', ')}${invalidRoles.length > 3 ? '...' : ''}. Roles must be from the list of unassigned roles.`
            : `Invalid role names found: ${invalidRoles.map(role => `"${role}"`).join(', ')}${invalidRoles.length > 3 ? '...' : ''}. Roles must be from the list of assigned roles.`;
          
          setValidationError(errorMessage);
          setRoleAssignmentData([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Validate ROLE_OWNER_EMAIL against available role owners
        const invalidOwnerRows = data.filter(
          row => !roleOwnerList.some(
            owner => owner.email === row.ROLE_OWNER_EMAIL
          )
        );
        
        if (invalidOwnerRows.length > 0) {
          const invalidEmails = [...new Set(invalidOwnerRows.map(row => row.ROLE_OWNER_EMAIL))].slice(0, 3);
          setValidationError(
            `Invalid role owner emails found: ${invalidEmails.map(email => `"${email}"`).join(', ')}${invalidEmails.length > 3 ? '...' : ''}. 
            All emails must belong to valid users in the system.`
          );
          setRoleAssignmentData([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        setRoleAssignmentData(data);
        setIsValidating(false);
        setFileProcessed(true); // Mark file as successfully processed
      } catch (error) {
        console.error('Error parsing CSV file:', error);
        setValidationError(`Error parsing CSV file: ${error.message}`);
        setIsValidating(false);
        setFileProcessed(false);
      }
    };
    
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      setValidationError(`Error reading file: ${error}`);
      setIsValidating(false);
      setFileProcessed(false);
    };
    
    reader.readAsText(file);
  };

  // Helper function to parse a CSV row
  const parseCSVRow = (row) => {
    const values = [];
    let inQuotes = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"' && (i === 0 || row[i - 1] !== '"')) {
        // Toggle inQuotes when encountering a single quote
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // If not in quotes, treat comma as separator
        values.push(currentValue);
        currentValue = '';
      } else {
        // Add char to the current value
        currentValue += char;
      }
    }
    values.push(currentValue); // Push the last value
    
    // Clean up quotes from the values
    return values.map(val => {
      // Remove surrounding quotes if they exist
      if (val.startsWith('"') && val.endsWith('"')) {
        return val.substring(1, val.length - 1);
      }
      return val;
    });
  };

  // Submit the role assignments
  const handleSubmitAssignments = async () => {
    setIsSubmitting(true);
    setSubmissionError('');
    setUploadProgress(0);
    
    try {
      // Transform the data to the format expected by the backend
      const formattedData = roleAssignmentData.map(row => {
        // Find the role owner by email address
        const roleOwner = roleOwnerList.find(owner => owner.email === row.ROLE_OWNER_EMAIL);
        if (!roleOwner) {
          throw new Error(`Could not find role owner with email: ${row.ROLE_OWNER_EMAIL}`);
        }

        return {
          APP_NAME: row.APP_NAME,
          ROLE_NAME: row.ROLE_NAME,
          ROLE_OWNERS: roleOwner.value // 'value' contains the ID as per our updated data format
        };
      });

      // Process each assignment - update existing or create new
      let processed = 0;
      const totalAssignments = formattedData.length;
      
      for (const data of formattedData) {
        // Check if this role assignment already exists
        const existingAssignment = await roleService.fetchRoleOwnerById(data.APP_NAME, data.ROLE_NAME);
        
        if (existingAssignment && existingAssignment.length > 0) {
          // Update the existing assignment
          await roleService.updateRoleOwner(existingAssignment[0].id, data);
          console.log(`Updated existing role assignment for ${data.ROLE_NAME}`);
        } else {
          // Create a new assignment
          await roleService.createRoleOwner(data);
          console.log(`Created new role assignment for ${data.ROLE_NAME}`);
        }
        
        processed++;
        // Update progress for visual feedback (0-100%)
        setUploadProgress(Math.round((processed / totalAssignments) * 100));
      }

      // Check if all roles are now assigned and update the ROLE_OWNER_SETUP flag
      try {
        // Fetch the current application details first
        const app = await appService.fetchAppsById(appId);
        console.log('App details fetched for ROLE_OWNER_SETUP update:', app);
        
        // After all assignments have been processed, check if there are any unassigned roles left
        const existingRoleOwners = await roleService.fetchRoleOwnersByApp(appId);
        console.log('Existing role owners:', existingRoleOwners.length);
        
        const allRoles = await appService.fetchAppsRecordById(appId);
        console.log('All roles for this app:', allRoles.length);
        
        const roleNames = allRoles.map(role => role.ROLE_NAME);
        const assignedRoleNames = existingRoleOwners.map(role => role.ROLE_NAME);
        
        console.log('Role names:', roleNames);
        console.log('Assigned role names:', assignedRoleNames);
        
        // Check if all roles now have owners
        const allRolesAssigned = roleNames.every(roleName => assignedRoleNames.includes(roleName));
        console.log('All roles assigned?', allRolesAssigned);
        console.log('Current ROLE_OWNER_SETUP value:', app.ROLE_OWNER_SETUP);
        
        // Update the app's ROLE_OWNER_SETUP flag based on whether all roles are assigned
        if (app && app.id) {
          // Only update if the current value doesn't match what it should be
          if (allRolesAssigned !== app.ROLE_OWNER_SETUP) {
            console.log('Updating ROLE_OWNER_SETUP from', app.ROLE_OWNER_SETUP, 'to', allRolesAssigned);
            const updateResult = await appService.updateApp(app.id, {
              ...app,
              ROLE_OWNER_SETUP: allRolesAssigned
            });
            console.log('Result of updating ROLE_OWNER_SETUP:', updateResult);
            console.log(`Updated ROLE_OWNER_SETUP to ${allRolesAssigned} for app ${appId}`);
          } else {
            console.log('ROLE_OWNER_SETUP already has the correct value:', allRolesAssigned);
          }
        } else {
          console.error('App object is invalid or missing ID:', app);
        }
      } catch (error) {
        console.error('Error updating ROLE_OWNER_SETUP flag:', error);
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        }
        // Continue with the success flow even if this update fails
      }

      // Add a small delay so users can see the 100% completion
      setTimeout(() => {
        setSubmissionSuccess(true);
        setIsSubmitting(false);
        if (onSuccess) {
          onSuccess(processed);
        }
      }, 500);
    } catch (error) {
      console.error('Error submitting role assignments:', error);
      setSubmissionError(`Error submitting role assignments: ${error.message}`);
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Calculate pagination values
  const pageCount = Math.ceil(roleAssignmentData.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, roleAssignmentData.length);
  const paginatedData = roleAssignmentData.slice(startIndex, endIndex);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ padding: '16px 24px' }}>
      {appName && (
        <Box sx={{ 
          mb: 3, 
          p: 1.5, 
          bgcolor: 'rgba(25, 118, 210, 0.08)', 
          borderRadius: '6px',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1
        }}>
          <Typography variant="body1" color="primary" sx={{ fontWeight: 500 }}>
            Application: <strong>{appName}</strong>
          </Typography>
        </Box>
      )}

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange} 
        sx={{ 
          mb: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          '.MuiTabs-indicator': {
            height: '3px',
            borderRadius: '3px 3px 0 0'
          },
          '.MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500
          }
        }}
      >
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography>Assign New Roles ({unassignedRolesList.length})</Typography>
            </Box>
          }
          disabled={unassignedRolesList.length === 0}
        />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography>Edit Existing Assignments ({assignedRolesList.length})</Typography>
            </Box>
          }
          disabled={assignedRolesList.length === 0}
        />
      </Tabs>

      {activeTab === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" sx={{ color: '#555' }}>
            Upload a CSV file to assign owners to multiple roles at once. First download the template with unassigned roles, fill in the role owner emails, and upload the completed file.
          </Typography>
          {unassignedRolesList.length > 0 && (
            <Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                size="small"
                sx={{ 
                  textTransform: 'none',
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: '#1976d2',
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                Download Template
              </Button>
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" sx={{ color: '#555' }}>
            Upload a CSV file to edit existing role owner assignments. First download the template with current assignments, modify the role owner emails as needed, and upload the completed file.
          </Typography>
          {assignedRolesList.length > 0 && (
            <Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                size="small"
                sx={{ 
                  textTransform: 'none',
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: '#1976d2',
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                Download Current Assignments
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      <Box sx={{ 
        mb: 2, 
        p: 1.5, 
        bgcolor: 'rgba(25, 118, 210, 0.08)', 
        borderRadius: '6px',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1
      }}>
        <Box>
          {activeTab === 0 ? (
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              <strong>{unassignedRolesList.length}</strong> roles are currently unassigned.
            </Typography>
          ) : (
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              <strong>{assignedRolesList.length}</strong> roles have existing assignments that can be edited.
            </Typography>
          )}
        </Box>
      </Box>
      
      {submissionSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite',
              pointerEvents: 'none'
            }
          }}
        >
          Successfully processed {roleAssignmentData.length} role assignments!
        </Alert>
      )}

      {submissionError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
        >
          {submissionError}
        </Alert>
      )}
      
      {!roleAssignmentData.length > 0 && !submissionSuccess && (
        <Paper
          elevation={0}
          sx={{
            border: dragging 
              ? '2px solid #1976d2' 
              : '2px dashed rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragging 
              ? 'rgba(25, 118, 210, 0.04)' 
              : '#fafafa',
            marginBottom: '20px',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('role-assignment-file-input').click()}
        >
          {isValidating && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
              <LinearProgress sx={{ height: 4 }} />
            </Box>
          )}
          
          <input
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            id="role-assignment-file-input"
          />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1.5, opacity: 0.7 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5, color: '#333' }}>
              Drag & drop your file here
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              or
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() => document.getElementById('role-assignment-file-input').click()}
              sx={{ 
                textTransform: 'none', 
                backgroundColor: '#1976d2',
                boxShadow: 'none',
                borderRadius: '6px',
                px: 2,
                '&:hover': {
                  backgroundColor: '#1565c0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              Browse Files
            </Button>
            <Typography variant="caption" sx={{ mt: 2, color: '#777' }}>
              Supported format: CSV file
            </Typography>
          </Box>
        </Paper>
      )}

      {/* File name display */}
      {fileName && !validationError && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: 'rgba(25, 118, 210, 0.08)', 
          p: 1.5, 
          pl: 2,
          borderRadius: '6px', 
          mb: 2
        }}>
          <DescriptionIcon sx={{ color: '#1976d2', mr: 1.5, fontSize: '1.25rem' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {fileName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {roleAssignmentData.length} rows • CSV file 
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClearFile}>
            <HighlightOffIcon sx={{ fontSize: '1.25rem', color: '#777' }} />
          </IconButton>
        </Box>
      )}

      {/* Validation Error message */}
      {validationError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-message': { fontWeight: 400 } 
          }}
        >
          {validationError}
        </Alert>
      )}

      {/* Data preview */}
      {roleAssignmentData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 500 }}>
              Data Preview
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Showing {startIndex + 1}-{endIndex} of {roleAssignmentData.length} rows
            </Typography>
          </Box>
          
          <Box sx={{ 
            border: '1px solid rgba(224, 224, 224, 1)', 
            borderRadius: '6px', 
            overflow: 'hidden',
            mb: 2
          }}>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
                    {Object.keys(roleAssignmentData[0]).map((header, index) => (
                      <th
                        key={index}
                        style={{
                          padding: '10px 16px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          color: '#333',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          whiteSpace: 'nowrap',
                          backgroundColor: requiredColumns.includes(header) ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)'
                        }}
                      >
                        {header}
                        {requiredColumns.includes(header) && (
                          <span style={{ color: '#1976d2', marginLeft: '2px' }}>*</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f9fafb' }}>
                      {Object.entries(row).map(([key, value], index) => (
                        <td
                          key={index}
                          style={{
                            fontSize: '0.75rem',
                            padding: '8px 16px',
                            borderBottom: rowIndex < paginatedData.length - 1 ? '1px solid rgba(224, 224, 224, 1)' : 'none',
                            color: '#333',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {value || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
          
          {/* Pagination controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                Rows per page:
              </Typography>
              <FormControl variant="standard" size="small">
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ 
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': { 
                      py: 0.5, 
                      minWidth: '60px' 
                    }
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.875rem',
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                },
                '& .Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFile}
              disabled={isSubmitting}
              sx={{ 
                textTransform: 'none',
                borderColor: 'rgba(0,0,0,0.12)',
                color: 'text.secondary',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={isSubmitting}
              onClick={handleSubmitAssignments}
              sx={{ 
                textTransform: 'none',
                borderRadius: '6px',
                position: 'relative',
                minWidth: '150px'
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress 
                    size={16} 
                    thickness={6} 
                    sx={{ 
                      color: 'white', 
                      mr: 1,
                      animation: 'pulse 1s ease-in-out infinite'
                    }} 
                  />
                  Processing... {uploadProgress > 0 && `${uploadProgress}%`}
                </Box>
              ) : 'Submit Assignments'}
            </Button>
          </Box>
        </Box>
      )}

      {isSubmitting && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#555' }}>
              Processing role assignments...
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
              {uploadProgress}%
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', height: 8, borderRadius: 4, overflow: 'hidden' }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ 
                height: '100%', 
                borderRadius: 4,
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundImage: 'linear-gradient(to right, #42a5f5, #1976d2)',
                  transition: 'transform 0.3s ease'
                }
              }}
            />
            {/* Overlay animation effect for the progress bar */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s infinite',
                pointerEvents: 'none'
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {uploadProgress < 100 
                ? 'Please wait while your role assignments are being processed...' 
                : 'All role assignments have been processed successfully!'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Processing {uploadProgress < 100 ? `${Math.ceil(uploadProgress * roleAssignmentData.length / 100)}/${roleAssignmentData.length}` : `${roleAssignmentData.length}/${roleAssignmentData.length}`} assignments
            </Typography>
          </Box>
        </Box>
      )}

      {fileProcessed && roleAssignmentData.length > 0 && !submissionSuccess && !isSubmitting && (
        <Alert 
          severity="info" 
          icon={<CloudDoneIcon />}
          sx={{ 
            mt: 3,
            mb: 2,
            '& .MuiAlert-message': { 
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center'
            } 
          }}
        >
          <Typography variant="body2">
            <strong>{fileName}</strong> with <strong>{roleAssignmentData.length} role assignments</strong> is ready to submit. Click "Submit Assignments" to proceed.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default BulkRoleAssignmentUpload; 